import { createApi, fetchBaseQuery, retry } from "@reduxjs/toolkit/query/react";
import type { BaseQueryFn, FetchArgs, FetchBaseQueryError } from "@reduxjs/toolkit/query";
import { getInstanceIdFromUrl } from "@/utils/instanceId";
import { getSavedTenantId, authService } from "@/utils/authService";
import type { IRootReducerState } from "@/store/IRootReducer";
import { logger } from "@/logger-init";
import { hasPhoneSession } from "@/utils/phoneSession";

const MAX_RETRIES = 3;

const fetchQuery = fetchBaseQuery({
  baseUrl: "/api/v1",
  prepareHeaders: async (headers, { getState }) => {
    const state = getState() as IRootReducerState;
    // A phone sign-in's pass comes from its session, which knows when it runs
    // out (and then goes back to the sign-in page); never a stale copy.
    let token = hasPhoneSession()
      ? await authService.getAccessToken()
      : state.authReducer?.accessToken;

    // Redux token may not be set yet on regular page loads (authReducer
    // is not persisted). Fall back to acquiring a token directly from MSAL.
    if (!token) {
      try {
        token = await authService.getAccessToken();
      } catch {
        // MSAL not ready or no accounts — proceed without token
      }
    }

    if (token) {
      headers.set("Authorization", `Bearer ${token}`);
    }

    // Always include instance ID in headers for tenant-scoped operations
    const instanceId = getInstanceIdFromUrl();
    if (instanceId) {
      headers.set("instanceid", instanceId);
    }

    // Include tenant ID for operations that require tenant-specific resources.
    // Prefer sessionStorage (authoritative after tenant switch) over Redux state
    // which may be stale during rehydration.
    const tenantId = getSavedTenantId() || state.tenantsReducer?.tenantsData?.selectedTenant?.id;
    if (tenantId) {
      headers.set("tenantid", tenantId);
    }

    return headers;
  },
});

/** A phone sign-in pass the server refused ends the phone session (see authService). */
const rawBaseQuery: BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError> = async (
  args,
  api,
  extraOptions,
) => {
  const result = await fetchQuery(args, api, extraOptions);
  if (result.error?.status === 401) authService.phonePassRefused(result.error.data);
  return result;
};

// Retry transient failures (5xx / network) ONLY for GET requests. Mutations
// (POST/PATCH/PUT/DELETE) are non-idempotent: silently resending one on a
// transient error duplicates its side effects — e.g. two "Ticket Assigned to
// You" emails + notification-log rows (#972) and duplicate pack-update audit
// rows (#908). We therefore gate retries by HTTP method. 401/403/429 are never
// retried (retrying won't help); this replaces the old post-hoc retry.fail()
// call, which ran after the retry loop and so never actually prevented retries.
//
// When retryCondition is supplied, maxRetries must be omitted (RTK's RetryOptions
// type is a discriminated union), so we cap attempts inside the condition. RTK
// passes attempt starting at 1 and increments per failure, so `attempt <=
// MAX_RETRIES` yields the same GET retry budget as the previous maxRetries: 3.
export const baseQueryWithRetry = retry(rawBaseQuery, {
  retryCondition: (error, args, { attempt }) => {
    const method = (typeof args === "string" ? "GET" : (args.method ?? "GET")).toUpperCase();
    if (method !== "GET") {
      return false;
    }

    const status = (error as FetchBaseQueryError).status;
    if (status === 401 || status === 403 || status === 429) {
      return false;
    }

    return attempt <= MAX_RETRIES;
  },
  backoff: async (attempt, _maxRetries) => {
    const delay = Math.min(1000 * Math.pow(2, attempt), 30000);
    await new Promise((resolve) => setTimeout(resolve, delay));
  },
});

// Paths that don't require an instance ID (global endpoints)
// These paths won't error if no instance is selected, but will still receive
// the instanceid header if one is available (added in prepareHeaders above)
const INSTANCE_ID_EXEMPT_PATHS = ["/admin/rbac"];

const isInstanceIdExempt = (url: string): boolean => {
  return INSTANCE_ID_EXEMPT_PATHS.some((path) => url.includes(path));
};

const baseQueryWithInstanceId: BaseQueryFn<
  string | FetchArgs,
  unknown,
  FetchBaseQueryError
> = async (args, api, extraOptions) => {
  const url = typeof args === "string" ? args : args.url;

  // Skip instance ID check for exempt paths (global endpoints)
  if (isInstanceIdExempt(url)) {
    const modifiedArgs: FetchArgs = typeof args === "string" ? { url: args } : { ...args };
    return baseQueryWithRetry(modifiedArgs, api, extraOptions);
  }

  const instanceId = getInstanceIdFromUrl();

  if (!instanceId) {
    logger.warn("[API] No instance ID available - API request may fail");
    return {
      error: {
        status: 400,
        statusText: "Bad Request",
        data: {
          error: "No instance selected",
          code: "MISSING_INSTANCE_ID",
          message:
            "Please select an instance to continue. Try refreshing the page or selecting a community from the navigation.",
        },
      } as FetchBaseQueryError,
    };
  }

  let modifiedArgs: FetchArgs;

  if (typeof args === "string") {
    const urlAlreadyHasInstanceId = args.toLowerCase().includes("instanceid=");
    if (urlAlreadyHasInstanceId) {
      modifiedArgs = { url: args };
    } else {
      const separator = args.includes("?") ? "&" : "?";
      modifiedArgs = {
        url: `${args}${separator}instanceId=${instanceId}`,
      };
    }
  } else {
    const url = args.url;
    const urlAlreadyHasInstanceId = url.toLowerCase().includes("instanceid=");
    const paramsInstanceId = args.params?.instanceId;
    const paramsHasValidInstanceId =
      typeof paramsInstanceId === "string" && paramsInstanceId.length > 0;
    if (urlAlreadyHasInstanceId || paramsHasValidInstanceId) {
      modifiedArgs = { ...args };
    } else {
      const separator = url.includes("?") ? "&" : "?";
      modifiedArgs = {
        ...args,
        url: `${url}${separator}instanceId=${instanceId}`,
      };
    }
  }

  return baseQueryWithRetry(modifiedArgs, api, extraOptions);
};

export const baseApi = createApi({
  reducerPath: "api",
  baseQuery: baseQueryWithInstanceId,
  keepUnusedDataFor: 60,
  refetchOnMountOrArgChange: 30,
  refetchOnFocus: true,
  refetchOnReconnect: true,
  tagTypes: [
    "SkyAgentProjects",
    "SkyAgentArtifacts",
    "SkyAgentSettings",
    "SkyAgentConversations",
    "SkyAgentAudit",
    "SkyAgentKnowledge",
    "SkyAgentSharing",
    "SkyAgentSchedules",
    "SkyAgentSubagents",
    "SkyAgentNotify",
    "SkyBrainSettings",
    "SkyBrainPrompt",
    "SkyBrainMemory",
    "SkyBrainPlaybooks",
    "SkyBrainConnectors",
    "SkyBrainOverview",
    "RBAC",
    "EntraIntegration",
    "IdpIntegration",
    "IdpGroupEntitlement",
    "DirectorySyncRun",
    "Agent",
    "AgentGroup",
    "Roles",
    "EndUsers",
    "UserDirectory",
    "EmailConfig",
    "Portal",
    "WorkItem",
    "WorkItemComment",
    "PortalWorkflow",
    "PortalMember",
    "PortalBoard",
    "RequestType",
    "PortalGroup",
    "Queue",
    "PortalRole",
    "Approval",
    "CustomField",
    "SlaPolicy",
    "RecurringTickets",
    "BusinessHours",
    "WorkItemSla",
    "AutomationRule",
    "Macro",
    "InstanceField",
    "Contact",
    "Organization",
    "KbCategory",
    "KbArticle",
    "CsatSettings",
    "CsatResponse",
    "Watcher",
    "HelpWidget",
    "Webhook",
    "QueueRoutingRule",
    "HubSpotConfig",
    "AiAgent",
    "AiConversation",
    "AiSkillProvider",
    "Form",
    "FormResponse",
    "CustomReport",
    "ScheduledReport",
    "ScheduledReportRun",
    "NotificationLog",
    "SavedView",
    "ReportSettings",
    "AssignmentMatrix",
    "TransitionRule",
    "StatusEntryRule",
    "AvailableTransitions",
    "Instance",
    "TicketAccess",
    "WorkforcePack",
    "WorkforceLocation",
    "WorkforceAsset",
    "WorkforcePerson",
    "WorkforceSuggestion",
    "WorkforceLicense",
    "WorkforceTicketTemplate",
    "WorkforceLifecycleRun",
    "WorkforceApproval",
    "WorkforceAudit",
    "WorkforceAccessGrant",
    "WorkforceAppApprover",
    "WorkforceDirectorySync",
    "WorkforceDashboard",
    "WorkforceReport",
    "WorkforceCertification",
    "WorkforceSodRule",
    "WorkforceAccessRequest",
    "WorkforceMe",
    "WorkforceMyRequest",
    "WorkforceMyOnboarding",
    "WorkforceMyTeam",
    "UamRegistry",
    "UamAppAccess",
    "UamAppGroups",
    "UamExternalConnection",
    "UserPreferences",
    "InstanceProducts",
    "EmployeeMe",
    "EmployeeMembers",
    "EmployeeScope",
    "EmployeeLocations",
    "EmployeeSettings",
    "EmployeeDirectory",
    "EmployeePerson",
    "EmployeeNotifications",
    "EmployeeFeed",
    "EmployeeApps",
    "EmployeeBanners",
    "EmployeeContent",
    "EmployeeSurveys",
    "EmployeeDue",
    "EmployeeModeration",
    "EmployeeRecognition",
    "EmployeeGiftcards",
    "EmployeeMessagingSettings",
    // Broadcasts (src/store/api/broadcastApi.ts).
    "EmployeeBroadcastPermissions",
    "EmployeeBroadcasts",
    "EmployeeBroadcastChannel",
    "EmployeeBroadcastFields",
    "EmployeeBroadcastEmailAddresses",
    "EmployeeBroadcastLists",
    "EmployeeBroadcastContacts",
    "EmployeeBroadcastTemplates",
    "EmployeeBroadcastReplies",
    "EmployeeBroadcastKeywords",
    "EmployeeBroadcastAutomations",
    "EmployeeLibrary",
    "EmployeeBenefits",
    "EmployeeConversations",
    "EmployeeMessages",
    "EmployeeMessageReports",
    "OrgChart",
  ],
  endpoints: () => ({}),
});

export default baseApi;
